import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Users, Ship, FileText, ShieldCheck, MessageSquare, Search,
  Loader2, AlertTriangle, CheckCircle2, Clock, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccountantDashboard = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [practice, setPractice] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, practiceRes] = await Promise.all([
        axios.get(`${API_URL}/api/accountant/clients`).catch(() => ({ data: { clients: [] } })),
        axios.get(`${API_URL}/api/accountant/practice`)
      ]);
      setClients(clientsRes.data.clients || []);
      setPractice(practiceRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setPractice({ status: 'pending' });
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-sky-400" />
      </div>
    );
  }

  if (practice?.status === 'pending') {
    return (
      <div className="max-w-lg mx-auto mt-20">
        <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-amber-500">
          <CardContent className="p-6 text-center">
            <Clock className="h-12 w-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Verification Pending</h2>
            <p className="text-slate-400 text-sm">
              Your practice registration is being reviewed. This usually takes 1-2 business days.
              You'll receive full access once your HMRC Agent Reference Number is verified.
            </p>
            <div className="mt-4 p-3 bg-slate-700/40 rounded-lg text-left">
              <p className="text-xs text-slate-500">Practice: <span className="text-white">{practice?.firm_name}</span></p>
              <p className="text-xs text-slate-500">HMRC ARN: <span className="text-white">{practice?.hmrc_arn}</span></p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (practice?.status === 'rejected') {
    return (
      <div className="max-w-lg mx-auto mt-20">
        <Card className="bg-slate-800 border-slate-700 border-l-4 border-l-red-500">
          <CardContent className="p-6 text-center">
            <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Verification Rejected</h2>
            <p className="text-slate-400 text-sm">
              Unfortunately your practice registration was not approved. 
              {practice?.rejection_reason && <span className="block mt-2 text-red-300">{practice.rejection_reason}</span>}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="accountant-dashboard">
      {/* GDPR Notice for Admin+ */}
      {practice?.superadmin_view && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-300">Admin+ View — Data Anonymized</p>
            <p className="text-xs text-slate-400 mt-0.5">
              Client personal data is redacted in compliance with GDPR. Use the Break-Glass tool on the Admin panel for emergency access (all access is audited).
            </p>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Client Dashboard</h1>
          <p className="text-slate-400 text-sm mt-0.5">
            {practice?.firm_name} — {clients.length} linked client{clients.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button onClick={() => navigate('/accountant/link-client')}
          className="bg-sky-600 hover:bg-sky-700 h-9 text-sm"
          data-testid="link-new-client-btn">
          <Users className="h-4 w-4 mr-2" />Link New Client
        </Button>
      </div>

      {/* Summary Cards */}
      {clients.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Clients</p>
              <p className="text-2xl font-bold text-white">{clients.length}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">SED Eligible</p>
              <p className="text-2xl font-bold text-emerald-400">{clients.filter(c => c.sed_eligible).length}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Trips</p>
              <p className="text-2xl font-bold text-white">{clients.reduce((s, c) => s + c.trip_count, 0)}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <p className="text-xs text-slate-500">Unread Messages</p>
              <p className="text-2xl font-bold text-sky-400">{clients.reduce((s, c) => s + c.unread_messages, 0)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      {clients.length > 3 && (
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input placeholder="Search clients..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-slate-800 border-slate-700 text-white h-9 text-sm"
            data-testid="client-search-input" />
        </div>
      )}

      {/* Client List */}
      {filtered.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">
              {clients.length === 0 ? 'No clients linked yet. Ask your clients to share their link code.' : 'No clients match your search.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(client => (
            <Card key={client.user_id}
              className="bg-slate-800 border-slate-700 hover:border-sky-500/40 transition-colors cursor-pointer"
              onClick={() => navigate(`/accountant/client/${client.user_id}`)}
              data-testid={`client-card-${client.user_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-sky-600/20 flex items-center justify-center shrink-0">
                      <span className="text-sky-400 font-semibold">{client.name?.charAt(0)?.toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-white truncate">{client.name}</p>
                        {client.sed_eligible && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 text-[10px] rounded-full shrink-0">
                            <CheckCircle2 className="h-2.5 w-2.5" />SED Eligible
                          </span>
                        )}
                        {client.unread_messages > 0 && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-sky-500/15 text-sky-400 text-[10px] rounded-full shrink-0">
                            <MessageSquare className="h-2.5 w-2.5" />{client.unread_messages}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">{client.email}</p>
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-6 text-xs text-slate-400 shrink-0 mr-2">
                    <div className="text-center">
                      <Ship className="h-3.5 w-3.5 mx-auto mb-0.5 text-slate-500" />
                      <span>{client.trip_count} trips</span>
                    </div>
                    <div className="text-center">
                      <FileText className="h-3.5 w-3.5 mx-auto mb-0.5 text-slate-500" />
                      <span>{client.document_count} docs</span>
                    </div>
                    <div className="text-center">
                      <ShieldCheck className="h-3.5 w-3.5 mx-auto mb-0.5 text-slate-500" />
                      <span>{client.qualifying_days}d qual.</span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-600 shrink-0" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountantDashboard;
